/**
 * Alexia Shop - Cookie Consent Store
 * ====================================
 * Pinia store for GDPR cookie consent management.
 */

import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { api } from '@/api/client'
import type { CookieConsent } from '@/types'

const STORAGE_KEY = 'cookie_consent'

export const useCookieStore = defineStore('cookies', () => {
  // ============================================
  // STATE
  // ============================================
  const consent = ref<CookieConsent>({
    strictly_necessary: true, // Always required
    functional: false,
    performance: false,
    targeting: false,
  })

  const isBannerVisible = ref(false)
  const isModalOpen = ref(false)
  const hasConsented = ref(false)

  // ============================================
  // GETTERS
  // ============================================
  const canUseFunctional = computed(() => consent.value.functional)
  const canUsePerformance = computed(() => consent.value.performance)
  const canUseTargeting = computed(() => consent.value.targeting)

  // ============================================
  // ACTIONS
  // ============================================

  /**
   * Load saved preferences from localStorage
   */
  function loadPreferences(): void {
    const saved = localStorage.getItem(STORAGE_KEY)

    if (saved) {
      try {
        const parsed = JSON.parse(saved) as CookieConsent
        consent.value = {
          ...consent.value,
          ...parsed,
          strictly_necessary: true, // Always true
        }
        hasConsented.value = true
        applyConsent()
      } catch {
        // Invalid data, show banner
        isBannerVisible.value = true
      }
    } else {
      // No saved preferences, show banner
      isBannerVisible.value = true
    }
  }

  /**
   * Accept all cookies
   */
  function acceptAll(): void {
    consent.value = {
      strictly_necessary: true,
      functional: true,
      performance: true,
      targeting: true,
    }
    saveAndApply()
  }

  /**
   * Reject all non-essential cookies
   */
  function rejectAll(): void {
    consent.value = {
      strictly_necessary: true,
      functional: false,
      performance: false,
      targeting: false,
    }
    saveAndApply()
  }

  /**
   * Save custom preferences
   */
  function savePreferences(prefs: Partial<CookieConsent>): void {
    consent.value = {
      ...consent.value,
      ...prefs,
      strictly_necessary: true, // Always true
    }
    saveAndApply()
  }

  /**
   * Save consent and apply settings
   */
  function saveAndApply(): void {
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent.value))
    hasConsented.value = true

    // Hide UI
    isBannerVisible.value = false
    isModalOpen.value = false

    // Apply consent settings
    applyConsent()

    // Sync to server (fire and forget)
    syncToServer()
  }

  /**
   * Apply consent settings (enable/disable scripts)
   */
  function applyConsent(): void {
    // Performance cookies (e.g., Google Analytics)
    if (consent.value.performance) {
      enableAnalytics()
    } else {
      disableAnalytics()
    }

    // Targeting cookies (e.g., Facebook Pixel)
    if (consent.value.targeting) {
      enableTargeting()
    } else {
      disableTargeting()
    }

    // Functional cookies
    if (consent.value.functional) {
      enableFunctional()
    }
  }

  /**
   * Sync consent to server for logged-in users
   */
  async function syncToServer(): Promise<void> {
    try {
      await api.post('/cookies/consent', consent.value)
    } catch {
      // Silently fail - localStorage is the source of truth
    }
  }

  // ============================================
  // COOKIE CONTROL FUNCTIONS
  // ============================================

  function enableAnalytics(): void {
    // Initialize Google Analytics if tracking ID is configured
    const gaId = import.meta.env.VITE_GA_TRACKING_ID
    if (gaId && !window.gtag) {
      // Load GA script
      const script = document.createElement('script')
      script.async = true
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`
      document.head.appendChild(script)

      // Initialize dataLayer
      window.dataLayer = window.dataLayer || []
      window.gtag = function() {
        window.dataLayer.push(arguments)
      }
      window.gtag('js', new Date())
      window.gtag('config', gaId)
    }
  }

  function disableAnalytics(): void {
    // Disable GA tracking
    const gaId = import.meta.env.VITE_GA_TRACKING_ID
    if (gaId) {
      (window as any)[`ga-disable-${gaId}`] = true
    }
  }

  function enableTargeting(): void {
    // Facebook Pixel, etc.
    // Implementation depends on specific services used
  }

  function disableTargeting(): void {
    // Remove targeting cookies if needed
  }

  function enableFunctional(): void {
    // Enable functional features (language preference, etc.)
  }

  // ============================================
  // UI CONTROLS
  // ============================================

  function showBanner(): void {
    isBannerVisible.value = true
  }

  function hideBanner(): void {
    isBannerVisible.value = false
  }

  function openModal(): void {
    isModalOpen.value = true
    isBannerVisible.value = false
  }

  function closeModal(): void {
    isModalOpen.value = false
  }

  return {
    // State
    consent,
    isBannerVisible,
    isModalOpen,
    hasConsented,
    // Getters
    canUseFunctional,
    canUsePerformance,
    canUseTargeting,
    // Actions
    loadPreferences,
    acceptAll,
    rejectAll,
    savePreferences,
    // UI
    showBanner,
    hideBanner,
    openModal,
    closeModal,
  }
})

// Type declarations for window globals
declare global {
  interface Window {
    dataLayer: any[]
    gtag: (...args: any[]) => void
  }
}
